import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styles from './TicketSuccessModal.module.scss';
import useClickAwayListener from '../../../hooks/useClickAwayLitsner';

export const CS_LOGGER_TRACKER_URL = 'https://sales.genio.in/sales_genio/?source=customersupport#/cs-logger-tracker';
export const GENIO_TME_HOME_URL = 'https://sales.genio.in/sales_genio/v2?source=customersupport#/mtd-dashboard';

const ICON_BASE_URL = 'https://akam.cdn.jdmagicbox.com/images/icontent/newwap/cssupport';
const TICK_ICON_URL = `${ICON_BASE_URL}/tick.svg`;
const HOME_ICON_URL = `${ICON_BASE_URL}/home.svg`;
const PLUS_ICON_URL = `${ICON_BASE_URL}/plus.svg`;

const PlusIcon = () => <img src={PLUS_ICON_URL} width="16" height="16" alt="" aria-hidden="true" />;

const HomeIcon = () => <img src={HOME_ICON_URL} width="17" height="17" alt="" aria-hidden="true" />;

const OPTIONS = [
  {
    key: 'anotherComplaint',
    title: 'Log Complaint for Another Company',
    url: CS_LOGGER_TRACKER_URL,
    desc: 'Raise another complaint without logging in again.',
    Icon: PlusIcon,
  },
  {
    key: 'genioHome',
    title: 'Return to Genio TME Home',
    url: GENIO_TME_HOME_URL,
    desc: 'Go directly to Genio TME Home / MTD Dashboard without closing existing session.',
    Icon: HomeIcon,
  },
];

export const HOME_PAGE_PATH = '/genio';

export default function TicketSuccessModal({ ticketId, handleClosePopup }) {
  const modalRef = useRef(null);
  const navigate = useNavigate();

  useClickAwayListener(modalRef, handleClosePopup);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClosePopup();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClosePopup]);

  const handleRedirect = (url) => {
    window.location.href = url;
  };

  const handleGoHome = () => {
    handleClosePopup();
    navigate(HOME_PAGE_PATH, { replace: true });
  };

  return (
    <section className={`cs_modal ${styles.Modal}`}>
      <div ref={modalRef} className={`cs_modal_content cs_modal_animation fadeInUpTop ${styles.Modal__Content}`}>
        <div className={styles.Modal__Bar} />
        <button type="button" className={styles.Modal__Close} onClick={() => handleClosePopup()} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 2L14 14M14 2L2 14" stroke="#777" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <div className={`cs_modal_body ${styles.Modal__Body}`}>
          <div className={styles.Modal__Header}>
            <div className={styles.SuccessIcon}>
              <img src={TICK_ICON_URL} width="22" height="22" alt="" aria-hidden="true" />
            </div>
            <div>
              <div className="font18 fw700 color111">Ticket created successfully!</div>
              <div className="font13 fw400 color777 mt-6">
                {ticketId ? (
                  <>
                    Your ticket <span className="fw600 color111">{ticketId}</span> has been created successfully.
                  </>
                ) : (
                  'Your ticket has been created successfully.'
                )}
              </div>
            </div>
          </div>

          <div className={`${styles.Divider} mt-18`} />

          <div className={`${styles.SectionLabel} font12 fw600 color777 mt-18`}>What would you like to do next?</div>

          {OPTIONS.map(({ key, title, url, desc, Icon }) => (
            <button key={key} type="button" className={`${styles.Option} mt-12`} onClick={() => handleRedirect(url)}>
              <span className={styles.Option__Head}>
                <span className={styles.Option__Icon}>
                  <Icon />
                </span>
                <span className={`${styles.Option__Title} font15 fw700 color111`}>{title}</span>
                <span className={styles.Option__Arrow}>
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                    <path
                      d="M1 1L7 7L1 13"
                      stroke="#777"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </span>
              <span className={`${styles.Option__Desc} font12 fw400 color777 mt-10`} style={{ display: 'block' }}>
                Redirects to <span className={styles.Option__Url}>{url}</span> — {desc}
              </span>
            </button>
          ))}

          <div className={`${styles.Divider} mt-20`} />

          <div className={`${styles.Modal__Footer} mt-15`}>
            <button type="button" className={`${styles.StayButton} font14 fw600 color111`} onClick={handleGoHome}>
              Go to Home Page
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

TicketSuccessModal.propTypes = {
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClosePopup: PropTypes.func,
};
